/**
 * Aging helpers for the manager deposit/withdrawal approval queues, so a
 * pending request never quietly rots: the queue can show "pending for N", sort
 * oldest-first, and flag anything past a soft SLA.
 */

/** Soft SLA (hours) for acting on a pending approval. Past this the row is
 *  flagged overdue. A control choice, not a regulatory deadline. */
export const APPROVAL_SLA_HOURS = 24;

export interface PendingAge {
  label: string; // "3d 4h" | "5h 30m" | "20m"
  hours: number;
  isStale: boolean; // pending >= the soft SLA
}

/** Human-readable time a request has been pending, plus the staleness flag. */
export function pendingAge(createdAtIso: string, now: number = Date.now()): PendingAge {
  const ms = Math.max(0, now - new Date(createdAtIso).getTime());
  const hours = ms / 3_600_000;
  const days = Math.floor(hours / 24);
  const h = Math.floor(hours % 24);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  let label: string;
  if (days > 0) label = `${days}d ${h}h`;
  else if (h > 0) label = `${h}h ${m}m`;
  else label = `${m}m`;
  return { label, hours, isStale: hours >= APPROVAL_SLA_HOURS };
}

/** Oldest-first, so the longest-waiting request surfaces at the top of the
 *  pending queue and gets actioned first. */
export function sortOldestFirst<T extends { created_at: string }>(rows: readonly T[]): T[] {
  return [...rows].sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
  );
}
